/**
 * The watchlist decision about a person, as a block of fields rather than a screen.
 *
 * ─── WHY IT IS NOT JUST PART OF THE DIALOG ───
 * It started life inside `PersonBlacklistDialog`, which is opened on somebody who is ALREADY
 * in the registry. That left the commonest case on this page — a face on camera that should
 * never have been on site — as three separate acts: register them, find them again in the
 * Registered People drawer, then open the dialog. Between the first and the third the person
 * is registered and NOT flagged, and if the operator is called away that is where they stay.
 *
 * So the same fields now appear inside the registration modals as an opt-in block, and the
 * fields are these ones rather than a second set that looks similar: two forms that drifted
 * would put a person on the list with a category the dialog cannot show and the evaluator
 * does not route on.
 *
 * ─── THE LABEL IS NOT THE MECHANISM ───
 * `label` ("visitor", "vip", …) once carried a "blacklist" value, and some registries still
 * hold it. It never did anything: alerting reads `is_blacklisted`, `blacklist_category`,
 * `blacklist_severity` and `blacklist_alert` (migrations 054/055), none of which a label can
 * set. Registering somebody as label="blacklist" therefore produced a person who LOOKED
 * flagged in the drawer and was invisible to the evaluator. Blacklisting from registration
 * is a real watchlist entry written by the real endpoint, and the Type dropdown is left
 * alone — it says what kind of visitor somebody is, not whether to interrupt anyone.
 */
import type { ReactNode } from 'react';
import {
  Field, WATCHLIST_INPUT, watchlistSeverityOf, WatchlistSeverityPicker,
  PERSON_WATCHLIST_CATEGORIES, WatchlistCategoryPicker, watchlistCategoryOf,
  DEFAULT_WATCHLIST_CATEGORY,
} from '../common/watchlistForm';

export interface PersonWatchlistValue {
  reason: string | null;
  /** blacklisted | wanted | missing — names the alert. See `watchlistForm`. */
  category: string;
  severity: string;
  alert: boolean;
}

/**
 * What a NEW entry starts as: barred, critical, alerting.
 *
 * The same defaults the node applies to a request that says nothing (see setBlacklist), so
 * ticking the box and saving immediately does exactly what it did before these fields existed.
 */
export const DEFAULT_PERSON_WATCHLIST: PersonWatchlistValue = {
  reason: null,
  category: DEFAULT_WATCHLIST_CATEGORY,
  severity: 'critical',
  alert: true,
};

/** The category record for a value, for callers that tint themselves with it. */
export const personWatchlistCategory = (value?: string | null) =>
  watchlistCategoryOf(PERSON_WATCHLIST_CATEGORIES, value);

/**
 * The on/off pill.
 *
 * A switch rather than a tick box wherever the question is "does this happen at all": on/off
 * behaviour and one-of-a-set selection read differently at a glance, and both of this file's
 * two toggles are the former.
 *
 * `trackOn` is passed in rather than fixed, so the control takes the colour of whatever it is
 * turning on — the category's hue inside the form, the brand's outside it.
 */
export function WatchlistSwitch({
  checked, onChange, disabled, trackOn = 'bg-red-500', label,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  /** A single background class. `muted-foreground/30` is NOT usable here — see below. */
  trackOn?: string;
  label?: string;
}) {
  return (
    <span className="mt-0.5 shrink-0">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
        aria-label={label}
        className="peer sr-only"
      />
      {/*
        The off track is a numeric grey on purpose. The theme's `muted-foreground` is a bare
        `var()`, and Tailwind v3 emits NO rule at all for an opacity modifier on one of those —
        `bg-muted-foreground/30` would have left the track with no background whatsoever.
      */}
      <span className={`flex h-5 w-9 items-center rounded-full p-0.5 transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-brand ${
        checked ? trackOn : 'bg-zinc-400/60'
      } ${disabled ? 'opacity-50' : ''}`}>
        <span className={`h-4 w-4 rounded-full bg-white shadow-sm transition-transform ${
          checked ? 'translate-x-4' : 'translate-x-0'
        }`} />
      </span>
    </span>
  );
}

/** A switch with its own explanation beside it — the shape both toggles here take. */
function SwitchRow({
  checked, onChange, disabled, trackOn, title, children,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  trackOn?: string;
  title: string;
  children: ReactNode;
}) {
  return (
    <label className={`flex items-start gap-3 px-3 py-2.5 ${disabled ? '' : 'cursor-pointer'}`}>
      <WatchlistSwitch
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        trackOn={trackOn}
        label={title}
      />
      <span className="min-w-0">
        <span className="block text-[13px] font-medium text-foreground">{title}</span>
        <span className="mt-0.5 block text-[11px] leading-snug text-muted-foreground">
          {children}
        </span>
      </span>
    </label>
  );
}

export interface PersonWatchlistFormProps {
  value: PersonWatchlistValue;
  onChange: (value: PersonWatchlistValue) => void;
  disabled?: boolean;
  /** Focus the reason box on mount — for the dialog, where it is the only text to type. */
  autoFocusReason?: boolean;
  /** Enter in the reason box. The dialog's confirm button lives outside the form element. */
  onEnter?: () => void;
}

/**
 * Category, reason, and the alerting block that owns severity.
 *
 * Alerting and severity are ONE bordered box with the ladder nested under the switch, because
 * severity only exists when the switch is on. They used to be sibling fields with severity
 * first, which asked the operator how loud to be before asking whether to speak at all.
 *
 * Severity is disabled rather than hidden when alerting is off: with no alert there IS no
 * severity, but a row that vanished would leave the operator unsure whether their setting had
 * been thrown away.
 */
export default function PersonWatchlistForm({
  value, onChange, disabled, autoFocusReason, onEnter,
}: PersonWatchlistFormProps) {
  const cat = personWatchlistCategory(value.category);
  const alertName = cat?.alertName || 'watchlist';
  const set = (patch: Partial<PersonWatchlistValue>) => onChange({ ...value, ...patch });

  return (
    <div className="space-y-4">
      {/* FIRST, because it is the decision the rest of the form only tunes: it is what the
          alert will be CALLED, and the response to a missing person is not the response to
          a barred one. */}
      <Field label="Listed as" hint={cat?.hint}>
        <WatchlistCategoryPicker
          categories={PERSON_WATCHLIST_CATEGORIES}
          value={value.category}
          onChange={(category) => set({ category })}
          disabled={disabled}
        />
      </Field>

      <Field
        label="Reason"
        hint="The detail the category cannot carry — a case number, who asked, what happened."
      >
        <input
          autoFocus={autoFocusReason}
          value={value.reason || ''}
          onChange={(e) => set({ reason: e.target.value })}
          onKeyDown={(e) => { if (e.key === 'Enter' && onEnter && !disabled) onEnter(); }}
          disabled={disabled}
          placeholder="Ex-contractor, barred visitor, FIR 214/26…"
          className={`${WATCHLIST_INPUT} disabled:opacity-50`}
        />
      </Field>

      <div className="overflow-hidden rounded-lg border border-border">
        <SwitchRow
          checked={value.alert}
          onChange={(alert) => set({ alert })}
          disabled={disabled}
          trackOn={cat?.solid?.split(' ')[0] || 'bg-red-500'}
          title="Raise an alert on every sighting"
        >
          {value.alert
            ? `Each recognition raises a “${alertName}” alert at the severity below.`
            : 'Sightings are still flagged, searchable and on the reports — they just will not '
              + 'raise an alert. Use this to watch somebody quietly.'}
        </SwitchRow>

        <div className="border-t border-border px-3 py-2.5">
          <Field
            label="Alert severity"
            hint={value.alert
              ? watchlistSeverityOf(value.severity)?.hint
              : 'No alert will be raised, so severity does not apply.'}
          >
            <WatchlistSeverityPicker
              value={value.severity}
              onChange={(severity) => set({ severity })}
              disabled={!value.alert || disabled}
            />
          </Field>
        </div>
      </div>
    </div>
  );
}

/**
 * The same form as an OPT-IN, for the registration modals.
 *
 * `null` means "not on the list", which is what registering somebody has always meant and
 * stays the default: the overwhelming majority of registrations are ordinary people, and a
 * form that opened with a watchlist category already chosen would be inviting the operator to
 * skim past it.
 *
 * Deliberately shaped like the "This person is an employee" block beside it — both answer
 * "and what else is true about them", both are reversible from the Registered People drawer
 * afterwards, and an operator should not have to learn two idioms on one modal.
 */
export function PersonWatchlistToggle({
  value, onChange, name, disabled,
}: {
  /** The entry to create, or null to register without one. */
  value: PersonWatchlistValue | null;
  onChange: (value: PersonWatchlistValue | null) => void;
  /** Who is being registered, for the consequence sentence. */
  name?: string;
  disabled?: boolean;
}) {
  const on = !!value;
  const cat = personWatchlistCategory(value?.category);
  const who = name?.trim() || 'this person';

  return (
    <div className="overflow-hidden rounded-lg border border-border bg-muted">
      <SwitchRow
        checked={on}
        onChange={(next) => onChange(next ? DEFAULT_PERSON_WATCHLIST : null)}
        disabled={disabled}
        trackOn={on ? cat?.solid?.split(' ')[0] || 'bg-red-500' : 'bg-red-500'}
        title="Put this person on the watchlist"
      >
        {on
          ? <>
              Every camera that recognises {who} will flag the sighting
              {value?.alert
                ? <> and raise a <span className="font-medium text-foreground">
                    {cat?.alertName || 'watchlist'}
                  </span> alert</>
                : ', with no alert raised'}.
            </>
          : 'Registered only — recognised and logged, with nothing flagged. You can list them '
            + 'at any time from the Registered People drawer.'}
      </SwitchRow>

      {on && value && (
        <div className="border-t border-border bg-card px-3 py-3">
          <PersonWatchlistForm value={value} onChange={onChange} disabled={disabled} />
        </div>
      )}
    </div>
  );
}
